import cn from 'classnames';
import {
  CheckPostcode,
  Button,
  Heading,
  HeadingEnum,
  TextColorEnum,
  ArrowBack,
  Icon,
  IconSizeEnum,
} from '@desquared/wave-components-library';
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

import GOOGLE_API_KEY from '@/utils/googleAPIKey';
import { getShopsForDeliveryV3 } from '@/api/Shops';
import { transformToStigmas } from '@/utils/addressUtilities';
import { Shop } from '@/store/shop';
import { PostalCode } from '@/store/isAddressRegistered';
import useDeliveryServices from '@/hooks/useDeliveryServices';
import { setShopId } from '@/utils/products/products';

import styles from './PostalCodeCheckPage.module.css';

export default function PostalCodeCheckPage({ config, logo }) {
  const router = useRouter();
  const [postalCode, setPostalCode] = useState('');
  const [successCode, setSuccessCode] = useState('');
  const [isSuccessFul, setIsSuccessFul] = useState(false);

  const [stigmas, setStigmas] = useState([]);
  const [defaultMapCenter, setDefaultMapCenter] = useState({
    lat: process.env.defaultMapCenter.lat,
    lon: process.env.defaultMapCenter.lng,
  });
  const t = useTranslations('common.asides.postal_check');
  const r = useTranslations();
  const a = useTranslations('common.checkout');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [hasError, setHasError] = useState(false);

  const { selectedService, setSelectedService, deliveryServices } = useDeliveryServices();
  const onPostalCodeChange = e => {
    setPostalCode(e);
    setSuccessCode('unknown');
    setPostalCodeError('');
    setHasError(false);
  };

  const onCheckClick = async () => {
    if (!postalCode || postalCode.trim() === '') {
      setHasError(true);
      setPostalCodeError(a('required_field'));
      return;
    }
    const numberIdNoSpace = postalCode.replace(/\s/g, '');
    const parsePattern = /^\d{5}$/;
    const isValid = numberIdNoSpace.match(parsePattern);
    if (isValid) {
      setPostalCodeError('');
      setHasError(false);

      submitPostalCode();
    } else {
      setHasError(true);
      setPostalCodeError(t('input_error'));
    }
  };

  useEffect(() => {
    const $shop = Shop.subscribe(shopData => {
      if (shopData?.selectedShop) {
        setDefaultMapCenter({
          lat: shopData.selectedShop.address.latitude,
          lon: shopData.selectedShop.address.longitude,
        });
      } else if (shopData?.takeAway) {
        setDefaultMapCenter({
          lat: shopData.takeAway[0].address.latitude,
          lon: shopData.takeAway[0].address.longitude,
        });
      }
    });

    const setUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const { latitude, longitude } = position.coords;
            setDefaultMapCenter({ lat: latitude, lon: longitude });
          },
          error => {
            console.error('Error getting user location:', error);
          }
        );
      } else {
        console.warn('Geolocation is not supported by this browser.');
      }
    };

    setUserLocation();

    return () => {
      $shop.unsubscribe();
    };
  }, [setDefaultMapCenter]);

  function findFirstTakeAwayEntry(entries) {
    return entries.find(entry => entry.deliversToAddress !== true);
  }

  function findFirstDeliveryEntry(entries) {
    return entries.find(entry => entry.deliversToAddress === true);
  }

  async function selectDelivery() {
    const newService = findFirstDeliveryEntry(deliveryServices);
    setSelectedService(newService);
    // const shop = await setShopId(PostalCode.getValue(), newService?.id);
    router.push('/');
  }

  function selectTakeAway() {
    const newService = findFirstTakeAwayEntry(deliveryServices);
    setSelectedService(newService);
    router.push('/select-service');
  }

  const submitPostalCode = async () => {
    const shops = await getShopsForDeliveryV3(selectedService?.id, postalCode);
    Shop.setShopByDeliveryId(selectedService?.id, shops);
    if (!shops || shops?.length === 0) {
      setSuccessCode('error');
      PostalCode.update(postalCode);
      setIsSuccessFul(false);
    } else {
      setSuccessCode('success');
      setIsSuccessFul(true);
      PostalCode.update(postalCode);
      const shop = await setShopId(postalCode, selectedService?.id);
      const newStigmas = transformToStigmas(shops);
      setStigmas(newStigmas);
    }
  };
  return (
    <div className={styles.outerContainer}>
      <div className={cn(styles.headerCategories)}>
        <button className={cn(styles.buttonContainerBoth, styles.back)} onClick={router.back}>
          <Icon size={IconSizeEnum.Medium} IconComponent={ArrowBack} iconColor={TextColorEnum.Gray1} />
        </button>
        <Heading
          color={TextColorEnum.Gray1}
          type={HeadingEnum.HeadingS}
          classOption={styles.title}
          style={{ fontWeight: 700 }}
        >
          {t('btn_label')}
        </Heading>
      </div>

      <CheckPostcode
        successCode={successCode}
        stigmas={stigmas}
        description={t('description')}
        label={t('input_label')}
        postCode={postalCode}
        successText={t('success', { serviceTitle: selectedService?.title })}
        errorText={t('error_text')}
        onClick={onPostalCodeChange}
        inputErrorText={postalCodeError}
        inputErrorTextType={hasError ? 'error' : 'success'}
        googleApiKey={GOOGLE_API_KEY}
        minLength={config?.fields?.address?.postalCode?.minLength ?? 0}
        maxLength={config?.fields?.address?.postalCode?.maxLength ?? 5}
        format={config?.fields?.address?.postalCode?.format ?? 'NUMERIC'}
        minLengthErrorText={r('common.minimun_character')}
        mapTilesLocale={router.locale}
        defaultCenter={defaultMapCenter}
        logo={logo}
      />
      <div className={styles.midButton}>
        <Button
          onClick={() => {
            onCheckClick();
          }}
          text={t('btn_label')}
        />
      </div>
      <div className={styles.footer}>
        <Button
          onClick={() => {
            isSuccessFul ? selectDelivery() : selectTakeAway();
          }}
          text={isSuccessFul ? t('keep_shoping') : t('other_service')}
          variant={'filled'}
          // disabled={!isSuccessFul}
        ></Button>
      </div>
    </div>
  );
}
